import {
  useWSSnackbar,
  WSElement,
  WSEmptyState,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction
} from "@wingspanhq/fe-component-library";
import React, { useCallback, useMemo } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";

import {
  AttributeDataType,
  WorkDefinition
} from "@wingspanhq/wingspan-api-sdk";
import { useSession } from "@hooks/useSession";
import { useQueryPayerEngagement } from "../../../../query/payerEngagements/queries/useQueryPayerEngagement";
import {
  queriesPayers,
  queriesWorkItems,
  queriesWorkLoggingConfigs,
  queriesWorkLogs
} from "@query";
import { selectorPayerResponseFromV3 } from "@selectors/selectorPayerResponseFromV3";
import { useQueryPayerRows } from "../../../../query/search/payer/queries/useQueryPayerRows";
import { useQueryEngagementsWorkDefinitions } from "../../../../query/workDefinition/queries/useQueryEngagementWorkDefinitions";
import {
  useModalWorkLogHistory,
  WorkLogHistoryWidget
} from "../../components/WorkLogHistoryWidget";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { selectorPayerDisplayName } from "../../../../shared/selectors/selectorPayerDisplayName";
import { AlertWorkLogReviewStatusPayee } from "../../components/AlertWorkLogReviewStatusPayee";
import { CardWorkItems } from "../../components/CardWorkItems";
import { WorkLogAdditionalDetailsEditable } from "../../components/WorkLogAdditionalDetailsEditable";
import { WorkLogAssignmentInfoPayeeView } from "../../components/WorkLogAssignmentInfoPayeeView";
import { WorkLogStatusStepperPayeeView } from "../../components/WorkLogStatusStepperPayeeView";
import { useModalDeleteWorkLog } from "../../modals/ModalDeleteWorkLog";
import { useModalRequestEditing } from "../../modals/ModalRequestEditing";
import { useModalSubmitForReview } from "../../modals/ModalSubmitForReview";
import { routeHelpersWorkLogsPayee } from "../../paths";
import {
  canPayeeDelete,
  canPayeeEdit,
  canPayeeRequestEditing,
  canPayeeSubmit,
  hasPayeeRequestedEditing,
  isStatusApproved
} from "../../utils/workLogPermissions";

export const RoutePayeeWorkLogDetails: React.FC<
  RouteComponentProps<{ workLogId: string }>
> = props => {
  const workLogId = props.match.params.workLogId;
  const { accountId } = useSession();
  const history = useHistory();

  // Query work log
  const queryWorkLog = queriesWorkLogs.getWorkLog.useQuery({ workLogId });
  const workLog = queryWorkLog.data;

  // Query payer information
  // TODO(v3): v1 `payerPayeeEngagementId` renamed to `payeeEngagementId`.
  const queryPayerRow = useQueryPayerRows(
    {
      filter: {
        "engagements.payerPayeeEngagementId": workLog?.payeeEngagementId
      }
    },
    {
      enabled: !!workLog?.payeeEngagementId
    }
  );

  const payerId = queryPayerRow.data?.[0]?.payerId;
  const payerPayeeEngagementId = workLog?.payeeEngagementId;

  const queryPayer = queriesPayers.getPayer.useQuery(
    { payerId: payerId! },
    { enabled: !!payerId, retry: false }
  );

  const queryPayerPayeeEngagement = useQueryPayerEngagement(
    payerId!,
    payerPayeeEngagementId!,
    {
      enabled: !!payerId && !!payerPayeeEngagementId
    }
  );

  const payer = queryPayer.data;
  const payerPayeeEngagement = queryPayerPayeeEngagement.data;

  // Query work definitions
  const queryWorkDefinitions = useQueryEngagementsWorkDefinitions(
    {
      payerId: payerId!,
      engagementId: payerPayeeEngagementId!,
      sort: { name: "asc" }
    },
    {
      enabled: !!(payerId && payerPayeeEngagementId)
    }
  );

  // Query payer's work log config (controls whether attachments are required on submission)
  const queryWorkLogConfigForPayee =
    queriesWorkLoggingConfigs.getWorkLoggingConfigForPayee.useQuery(
      { payerAccountId: payerId as string },
      { enabled: !!payerId }
    );

  // Query work items
  const queryWorkItems = queriesWorkItems.listWorkItems.useQuery({
    filter: { workLogId: { eq: workLogId } }
  });

  // v3 `listWorkDefinitions` is account-scoped, so the payee can't list the
  // payer's work definitions off it — keep the v1 payerEngagements endpoint and
  // adapt its shape to v3 `WorkDefinition` for the (now v3) CardWorkItems.
  // TODO(v3): payee-side work-definition read stays on v1 payerEngagements; no
  // account-scoped v3 source exists for a payer's work definitions.
  const workDefinitions = useMemo<WorkDefinition[]>(
    () =>
      (queryWorkDefinitions.data || []).map(
        wd =>
          ({
            id: wd.workDefinitionId,
            name: wd.name,
            description: wd.description,
            attributeDefinitions: (wd.attributeDefinitions || []).map(attr => ({
              key: attr.key,
              name: attr.name,
              description: attr.description,
              type: attr.type as unknown as AttributeDataType,
              isRequired: attr.required,
              validationRules: attr.validationRules
            }))
          }) as unknown as WorkDefinition
      ),
    [queryWorkDefinitions.data]
  );

  // Modals and mutations for payee actions
  const modalSubmitForReview = useModalSubmitForReview();
  const modalDeleteWorkLog = useModalDeleteWorkLog();
  const modalRequestEditing = useModalRequestEditing();
  const modalWorkLogHistory = useModalWorkLogHistory();
  const { openSnackbar } = useWSSnackbar();

  // Browser page title
  const browserTitle = useMemo(() => {
    if (!payerPayeeEngagementId || !payer) {
      return "Work Log Details";
    }

    const payerName = selectorPayerDisplayName(payer);
    return payerPayeeEngagement
      ? `Work Log – ${payerName} • ${payerPayeeEngagement.engagementName}`
      : `Work Log – ${payerName}`;
  }, [payer, payerPayeeEngagement, payerPayeeEngagementId]);

  useBrowserPageTitle(browserTitle);

  // Derived state for work log status
  const status = workLog?.status;
  const canEdit = canPayeeEdit(status);
  const canSubmit = canPayeeSubmit(status);
  const canDelete = canPayeeDelete(status);
  const canRequestEditing = canPayeeRequestEditing(status);
  const editingRequested = hasPayeeRequestedEditing(workLog as any);
  const isApproved = isStatusApproved(status);

  const attachmentsRequired =
    queryWorkLogConfigForPayee.data?.requireAttachments?.enabled === true;
  const attachmentsMissing =
    attachmentsRequired && (workLog?.attachmentIds?.length ?? 0) === 0;

  // Other party name for per-item "Last edited by" pill
  const payerName = payer ? selectorPayerDisplayName(payer) : undefined;

  // Action handlers
  const handleViewInvoice = useCallback(() => {
    // TODO(v3): `invoiceId` renamed to `convertedResourceId` on v3 WorkLog.
    history.push(`/member/invoices/${workLog?.convertedResourceId}`);
  }, [workLog?.convertedResourceId, history]);

  const handleSubmitForReview = useCallback(() => {
    if (workLog) {
      modalSubmitForReview.open({
        workLog,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, modalSubmitForReview, queryWorkLog]);

  const handleDeleteWorkLog = useCallback(() => {
    if (workLog) {
      modalDeleteWorkLog.open({
        workLog,
        onClose: result => {
          if (result?.success) {
            history.push(routeHelpersWorkLogsPayee.path);
          }
        }
      });
    }
  }, [workLog, modalDeleteWorkLog, history]);

  const handleRequestEditing = useCallback(() => {
    if (workLog) {
      const clientName = payer ? selectorPayerDisplayName(payer) : undefined;
      modalRequestEditing.open({
        workLog,
        clientName,
        onClose: result => {
          if (result?.success) {
            queryWorkLog.refetch();
          }
        }
      });
    }
  }, [workLog, payer, modalRequestEditing, queryWorkLog]);

  const handleCancelEditingRequest = useCallback(() => {
    // TODO(v3): no v3 twin for the payee comment-only "cancel editing request"
    // (transitions take no comment; update is notes/attachments only). Accepted
    // breakage: the payee comment is not cleared server-side — refresh only.
    if (workLog) {
      openSnackbar({
        icon: { name: "check" },
        message: "Editing request cancelled"
      });
      queryWorkLog.refetch();
    }
  }, [workLog, openSnackbar, queryWorkLog]);

  const handleViewHistory = useCallback(() => {
    modalWorkLogHistory.open({ workLogId });
  }, [modalWorkLogHistory, workLogId]);

  // Build breadcrumbs following route structure: Work logs -> Work log #
  // NOTE: This must be called before any early returns to satisfy React hooks rules
  const breadcrumbs = useMemo(
    () => [
      {
        label: "Work logs",
        onClick: () => history.push(routeHelpersWorkLogsPayee.path)
      },
      {
        label: workLog ? `Work log #${workLog.workLogNumber}` : "Work log"
      }
    ],
    [history, workLog]
  );

  // Build toolbar actions based on work log status
  const toolbarActions = useMemo(() => {
    const actions: WSPageToolbarAction[] = [];

    // Submit for review - Primary button for Draft/InProgress
    if (canSubmit) {
      const workItemsCount = queryWorkItems.data?.data?.length || 0;
      actions.push({
        label: "Submit for review",
        buttonKind: "Primary",
        onClick: handleSubmitForReview,
        disabled: workItemsCount === 0 || attachmentsMissing,
        buttonOrder: 1
      });
    }

    // Request editing / Cancel request for editing - shown when work log is in review
    if (canRequestEditing) {
      if (editingRequested) {
        actions.push({
          label: "Cancel request for editing",
          buttonKind: "Secondary",
          onClick: handleCancelEditingRequest,
          buttonOrder: 1
        });
      } else {
        actions.push({
          label: "Request editing",
          buttonKind: "Secondary",
          onClick: handleRequestEditing,
          buttonOrder: 1
        });
      }
    }

    // Menu items (shown in three-dot menu)

    // View invoice - menu item for Approved status
    // TODO(v3): `invoiceId` renamed to `convertedResourceId` on v3 WorkLog.
    if (isApproved && workLog?.convertedResourceId) {
      actions.push({
        label: "View invoice",
        icon: "dollar-circle",
        onClick: handleViewInvoice
      });
    }

    // Delete work log (destructive)
    if (canDelete) {
      actions.push({
        label: "Delete work log",
        icon: "trash",
        onClick: handleDeleteWorkLog,
        destructive: true
      });
    }

    return actions;
  }, [
    isApproved,
    workLog?.convertedResourceId,
    canSubmit,
    canDelete,
    canRequestEditing,
    editingRequested,
    handleViewInvoice,
    handleSubmitForReview,
    handleDeleteWorkLog,
    handleRequestEditing,
    handleCancelEditingRequest,
    queryWorkItems.data,
    attachmentsMissing
  ]);

  // Error handling - 404 Not Found
  if (queryWorkLog.isError && queryWorkLog.error?.response?.status === 404) {
    return (
      <WSPage>
        <WSEmptyState
          actions={{
            buttons: [
              {
                label: "Back to work logs",
                onClick: () => history.push(routeHelpersWorkLogsPayee.path)
              }
            ]
          }}
          description="The work log you're looking for doesn't exist or has been deleted."
          title="Work log not found"
          type="search"
        />
      </WSPage>
    );
  }

  // Error handling - 403 Forbidden
  if (queryWorkLog.isError && queryWorkLog.error?.response?.status === 403) {
    return (
      <WSPage>
        <WSElement mt="2XL">
          <WSEmptyState
            actions={{
              buttons: [
                {
                  label: "Back to work logs",
                  onClick: () => history.push(routeHelpersWorkLogsPayee.path)
                }
              ]
            }}
            description="You don't have permission to view this work log."
            title="Access denied"
            type="error"
          />
        </WSElement>
      </WSPage>
    );
  }

  // Main content
  return (
    <WSPage>
      <WSPageToolbar
        actions={toolbarActions}
        breadcrumbs={breadcrumbs}
        title={workLog ? `Work log #${workLog.workLogNumber}` : "Work log"}
      />

      <AlertWorkLogReviewStatusPayee
        workLog={workLog}
        clientName={payerName}
        onCancelEditingRequest={
          editingRequested ? handleCancelEditingRequest : undefined
        }
        onViewHistory={handleViewHistory}
      />

      <WSGrid gutter="XL" mt="L">
        <WSGrid.Item span={{ m: "8" }}>
          <WSList gap="XL">
            <WorkLogAssignmentInfoPayeeView
              payer={payer ? selectorPayerResponseFromV3(payer) : undefined}
              payerPayeeEngagement={payerPayeeEngagement}
              shimmer={queryPayerPayeeEngagement.isInitialLoading}
              workLog={workLog}
            />

            <CardWorkItems
              currentUserRole="payee"
              noEdit={!canEdit}
              otherPartyName={payerName}
              payerId={payerId!}
              payerPayeeEngagementId={workLog?.payeeEngagementId!}
              resources={{ payerPayeeEngagement }}
              shimmer={
                queryWorkItems.isInitialLoading ||
                queryWorkDefinitions.isInitialLoading
              }
              workDefinitions={workDefinitions}
              workItems={queryWorkItems.data?.data || []}
              workLogId={workLogId}
              onViewHistory={handleViewHistory}
            />
          </WSList>
        </WSGrid.Item>

        <WSGrid.Item span={{ m: "4" }}>
          <WSList gap="XL">
            <WorkLogStatusStepperPayeeView
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <WorkLogAdditionalDetailsEditable
              attachmentsRequired={attachmentsRequired}
              payeeId={accountId}
              payerId={payerId}
              shimmer={queryWorkLog.isInitialLoading}
              workLog={workLog}
            />
            <WorkLogHistoryWidget workLogId={workLogId} />
          </WSList>
        </WSGrid.Item>
      </WSGrid>
    </WSPage>
  );
};
